import { getViewSwitcherSubMenuProps } from './getViewSwitcherSubMenuProps';
import { CalendarModuleCommandBarProps } from '../schema';
import { DefaultButton, IconButton } from '@fluentui/react/lib/Button';
import { IContextualMenuItem, IContextualMenuRenderItem } from '@fluentui/react/lib/ContextualMenu';
import { logUsage } from 'owa-analytics';
import { getNonNullCalendarsFromCalendarIds } from 'owa-calendar-cache';
import { getSelectedCalendars } from 'owa-calendar-surface-calendars-store';
import { getNumDaysInDayRange, resetNumDaysInDayRange } from 'owa-calendar-surface-options-utils';
import { selectCalendar, unselectCalendar } from 'owa-calendar-surface-select-calendar';
import { surfaceStore } from 'owa-calendar-surface-surface-store';
import { processSplitViewModeToggle } from 'owa-calendar-surface-view-store';
import { CalendarViewTypeEnum, DateRangeTypeToCalendarViewTypeEnum } from 'owa-calendar-view-type';
import { CharmControl, CharmControlColorPattern } from 'owa-charmcontrol';
import { ControlIcons } from 'owa-control-icons';
import { isFeatureEnabled } from 'owa-feature-flags';
import { FilterableMenuItem, FilterableMenuItemArgs } from 'owa-filterable-menu';
import loc from 'owa-localize';
import { getPalette } from 'owa-theme';
import * as React from 'react';
import {
    lazyShowAddOrEditSaveCalendarDialog,
    lazyShowDeleteSavedViewDialog,
} from 'owa-save-calendar-view-dialog';
import {
    SaveCalendarViewConfig,
    lazyGetSavedCalendarViewsMap,
    lazyGetCurrentlyDisplayedViewId,
    lazyUpdateCurrentlyDisplayedViewId,
} from 'owa-save-calendar-view-data';
import {
    saveViewDeleteSubMenuText,
    saveViewEditSubMenuText,
} from 'owa-calendar-shared-strings/lib/strings/calendarSurfaceCommandBar.locstring.json';

const styles = require('../../CalendarModuleCommandBar.scss');

export function getSurfaceSavedViewMenuItems(
    props: CalendarModuleCommandBarProps,
    viewSwitcherButtonRef: React.RefObject<IContextualMenuRenderItem>
): FilterableMenuItem[] {
    if (!isFeatureEnabled('cal-saveCalendarView')) {
        return [];
    }

    const isSaveViewSelected = (savedView: SaveCalendarViewConfig) => {
        const getCurrentlyDisplayedViewId = lazyGetCurrentlyDisplayedViewId.tryImportForRender();
        return getCurrentlyDisplayedViewId && savedView.id == getCurrentlyDisplayedViewId();
    };

    const getSavedCalendarViewsMap = lazyGetSavedCalendarViewsMap.tryImportForRender();
    if (!getSavedCalendarViewsMap) {
        return [];
    }

    const getSavedViewsFilterableMenuItemOnRender = (
        savedView: SaveCalendarViewConfig
    ): ((item: any) => React.ReactNode) => item => {
        const savedViewSubmenuIcon = {
            iconName: ControlIcons.More,
            className: styles.viewMenuIcon,
        };
        const onSavedViewClick = () => {
            logUsage('changeViewToSaveView');
            selectSavedView(savedView, props);
            viewSwitcherButtonRef.current.dismissMenu();
            if (getNumDaysInDayRange() > 1) {
                resetNumDaysInDayRange();
            }
        };
        const onDeleteSaveViewClick = (ev: React.MouseEvent<HTMLElement>) => {
            logUsage('deleteSaveView');
            lazyShowDeleteSavedViewDialog.importAndExecute(savedView);
            viewSwitcherButtonRef.current.dismissMenu();
        };
        const onEditSaveViewClick = (ev: React.MouseEvent<HTMLElement>) => {
            lazyShowAddOrEditSaveCalendarDialog.importAndExecute(savedView);
            viewSwitcherButtonRef.current.dismissMenu();
        };
        const savedViewSubMenuItems: IContextualMenuItem[] = [
            {
                text: loc(saveViewEditSubMenuText),
                key: loc(saveViewEditSubMenuText),
                title: loc(saveViewEditSubMenuText),
                onClick: onEditSaveViewClick,
            },
            {
                text: loc(saveViewDeleteSubMenuText),
                key: loc(saveViewDeleteSubMenuText),
                title: loc(saveViewDeleteSubMenuText),
                onClick: onDeleteSaveViewClick,
            },
        ];

        const palette = getPalette();
        const charmColorProps = {
            colorPattern: CharmControlColorPattern.Normal,
            calPrimary: palette.themePrimary,
            calDark: palette.themeDark,
            calDarkAlt: palette.themeDarkAlt,
            textColor: palette.neutralPrimary,
        };

        return (
            <div>
                <DefaultButton
                    className={styles.savedViewDefaultButton}
                    onClick={onSavedViewClick}
                    ariaLabel={savedView.viewName}
                    styles={{
                        label: styles.viewMenuButtonLabel,
                        flexContainer: styles.savedViewFlexContainer,
                    }}>
                    <CharmControl
                        iconId={parseInt(savedView.chosenHotkey)}
                        className={styles.charm}
                        colorProps={charmColorProps}
                    />
                    <span className={styles.savedViewName}>{savedView.viewName}</span>
                </DefaultButton>
                <IconButton
                    menuProps={getViewSwitcherSubMenuProps(savedViewSubMenuItems)}
                    menuIconProps={savedViewSubmenuIcon}
                    className={styles.savedViewIconButton}
                />
            </div>
        );
    };

    const savedViewsMapValues = getSavedCalendarViewsMap().values();
    const savedViews: FilterableMenuItem[] = [];
    for (let savedView of savedViewsMapValues) {
        savedViews.push(
            new FilterableMenuItem({
                [FilterableMenuItemArgs.key]: savedView.id,
                [FilterableMenuItemArgs.onRender]: getSavedViewsFilterableMenuItemOnRender(
                    savedView
                ),
                [FilterableMenuItemArgs.title]: savedView.viewName,
                [FilterableMenuItemArgs.className]: isSaveViewSelected(savedView)
                    ? styles.viewOptionSelected
                    : null,
            })
        );
    }
    if (!savedViews.length) {
        return [];
    }
    return [
        new FilterableMenuItem({
            [FilterableMenuItemArgs.key]: 'SavedViewDivider',
            [FilterableMenuItemArgs.name]: '-',
        }),
        ...savedViews,
    ];
}

const SINGLE_DAY_VIEW_ONE_DAY = 1;

function selectSavedView(savedView: SaveCalendarViewConfig, props: CalendarModuleCommandBarProps) {
    const { onViewTypeChange } = props;
    const savedViewCalendars = getNonNullCalendarsFromCalendarIds(savedView.selectedCalendarIds);
    savedViewCalendars.forEach(calendar => {
        selectCalendar(calendar);
    });

    const currentSelectedCalendars = getSelectedCalendars();
    for (let calendar of currentSelectedCalendars) {
        if (!savedView.selectedCalendarIds.includes(calendar.id.id)) {
            unselectCalendar(calendar);
        }
    }

    const viewType = DateRangeTypeToCalendarViewTypeEnum(savedView.dateRangeType);
    switch (viewType) {
        //TODO VSO 90248: Add extended functionality for "Day" daterangetype
        case CalendarViewTypeEnum.Daily:
            onViewTypeChange?.(viewType, SINGLE_DAY_VIEW_ONE_DAY);
            break;
        default:
            onViewTypeChange?.(viewType);
            break;
    }
    if (savedView.splitView !== surfaceStore.isInSplitView) {
        processSplitViewModeToggle(surfaceStore);
    }

    lazyUpdateCurrentlyDisplayedViewId.importAndExecute(savedView.id);
}
